<script>
import { cloneObject } from "../../helpers/common";
import { generateQueryString } from "../../helpers/uri";

export default {
  props: {
    setDelay: {
      type: Number,
      default: 500,
    },
    dataUrl: {
      type: String,
    },
    dataSrc: {
      type: Object | Array,
    },
    groupBy: {
      type: String | Function,
    },
    groupLoadKey: {
      type: String,
    },
    groupByRemote: {
      type: String,
    },
    orderByDefault: {
      type: String,
    },
    orderTypeDefault: {
      type: String,
    },
    initialData: {
      type: Object | Array,
    },
    initialSearch: {
      type: String,
    },
    defaultFilter: {
      type: Object,
      default() {
        return { };
      },
    },
    initialFilter: {
      type: Object,
      default() {
        return undefined;
      },
    },
    loader: {
      type: Function,
    },
    firstLoadParams: {
      type: Object,
      default() {
        return {};
      },
    },
    setLoadedData: {
      type: Boolean,
      default: true,
    },
    idKey: {
      type: String,
    },
  },
  data() {
    return {
      search: this.initialSearch,
      filter: this.initialFilter,
      orderBy: this.orderByDefault,
      orderType: this.orderTypeDefault,
      data: this.dataSrc,
      groupsLoaded: [],
      orderType: undefined,
      isLoading: false,
    };
  },
  watch: {
    dataSrc: function (newSource) {
      if (!this.setLoadedData) {
        this.data = newSource;
      }
    },
    initialSearch: function (newSearch) {
      this.set("search", newSearch);
    },
    initialFilter: function (newFilter) {
      this.filter = newFilter;

      if (this.dataUrl) {
        this.loadData();
      }
    },
    initialData: {
      immediate: true,
      handler: function (newData) {
        if (newData != undefined) {
          let oldData = "initialDataOld" in this ? this.initialDataOld : null;

          // remove items that have been in oldData but aren't in newData
          if (oldData && Array.isArray(newData) && Array.isArray(oldData)) {
            oldData.forEach((item) => {
              if (this.findItemIndex(item, newData) == -1) {
                this.findAndRemoveOrReplace(item);
              }
            });
          }

          this.initialDataOld = cloneObject(newData);
          this.setData(cloneObject(newData), this.data != undefined);
        }
      },
    },
  },
  computed: {
    allFilter() {
      return this.defaultFilter || this.filter
        ? Object.assign(
            {},
            this.defaultFilter ? this.defaultFilter : {},
            this.filter ? this.filter : {}
          )
        : undefined;
    },
    isFiltered() {
      return this.filter ? true : false;
    },
    dataGrouped() {
      let grouped = {};

      if (this.data) {
        this.data.forEach((item) => {
          let groupByVal = null;

          if (typeof this.groupBy == "function") {
            groupByVal = this.groupBy(item);
          } else {
            groupByVal = this.groupBy in item ? item[this.groupBy] : null;
          }

          if (groupByVal !== null) {
            if (!this.groupsLoaded.includes(groupByVal)) {
              this.groupsLoaded.push(groupByVal);
            }
            if (!(groupByVal in grouped)) {
              grouped[groupByVal] = [];
            }

            grouped[groupByVal].push(item);
          }
        });
      }

      return grouped;
    },
  },
  created() {
    if (this.dataUrl) {
      this.loadData({}, this.data != undefined);
    }
  },
  methods: {
    setSearch(search) {
      this.set("search", search);
    },
    setFilter(filter) {
      this.set("filter", filter);
    },
    setOrderBy(orderBy) {
      this.set("orderBy", orderBy);
    },
    setOrderType(orderType) {
      this.set("orderType", orderType);
    },
    set(property, value) {
      if (this.setTimeout) {
        window.clearTimeout(this.setTimeout);
        this.setTimeout = null;
      }

      this.setTimeout = window.setTimeout(
        (p, v) => {
          this.$set(this, p, v);

          if (this.dataUrl) {
            this.loadData();
          }
        },
        this.setDelay,
        property,
        value
      );
    },
    setFilterKey(key, value = undefined) {
      let filter = this.filter ? { ...this.filter } : {};

      if (value === undefined && key in filter) {
        delete filter[key];
      } else if (value !== undefined) {
        filter[key] = value;
      }

      this.set("filter", Object.keys(filter).length ? filter : undefined);
    },
    getFilterKey(key, alt = undefined) {
      if (this.allFilter && key in this.allFilter) {
        return this.allFilter[key];
      }

      return alt;
    },
    clearFilter() {
      this.set("filter", undefined);
    },
    setData(newData, merge = false, addMode = "push") {
      if (merge && Array.isArray(this.data) && Array.isArray(newData)) {
        if (this.idKey) {
          newData.forEach((item, index) => {
            let idVal = this.idKey in item ? item[this.idKey] : null;
            let itemIndex =
              idVal != null
                ? this.data.findIndex(
                    (p) => this.idKey in p && p[this.idKey] == idVal
                  )
                : -1;

            if (itemIndex != -1) {
              this.data.splice(itemIndex, 1, item);
            } else {
              this.data[addMode](item);
            }
          });
        } else {
          this.data = this.data.concat(newData);
        }
      } else {
        this.data = newData;
      }
    },
    loadData(params = {}, merge = false) {
      if (this.data == undefined && Object.keys(params).length == 0) {
        params = this.firstLoadParams;
      }

      return new Promise((resolve, reject) => {
        if (this.isLoading) {
          reject();
          return false;
        }

        this.isLoading = true;

        let url = this.dataUrl;

        params.search = this.search;
        params.filter = this.allFilter;
        params.orderby = this.orderBy;
        params.orderType = this.orderType;
        params.groupby = this.groupByRemote;

        let loader = this.loader;

        if (loader) {
          loader = loader(url, params, this.isFiltered);
        } else {
          let queryString = generateQueryString(params);
          if (queryString.length) {
            url += (url.indexOf("?") != -1 ? "&" : "?") + queryString;
          }

          loader = fetch(url)
            .then((resp) => {
              return resp.json();
            })
            .then((json) => {
              if ("success" in json) {
                if (json.success && "data" in json) {
                  return json.data;
                } else {
                  throw json;
                }
              } else {
                return json;
              }
            });
        }

        loader
          .then((data) => {
            if (this.setLoadedData) {
              this.setData(data, merge);
            }

            this.isLoading = false;
            resolve(data);
          })
          .catch((err) => {
            console.log("loading error", err);
            reject(err);
          });
      });
    },
    /**
     * Loads data groups.
     * Calls the loadData method with the group-key and the groups to load (array) as param.
     *
     * @param {Array|String} groups The group(s) to load.
     * @param {Boolean} [reloadIfExists=false] If it shall load a already loaded group again.
     * @param {String} [key] The group key. Will use the prop 'groupLoadKey' if not set.
     */
    loadGroups(groups, reloadIfExists = false, key = undefined) {
      if (this.isLoading) {
        return false;
      }
      if (key == undefined) {
        key = this.groupLoadKey;
      }
      if (!Array.isArray(groups)) {
        groups = [groups];
      }

      let groupsToLoad = [];

      for (let i in groups) {
        if (reloadIfExists || !this.groupsLoaded.includes(groups[i])) {
          groupsToLoad.push(groups[i]);
        }
      }

      if (groupsToLoad.length) {
        this.groupsLoaded = this.groupsLoaded.concat(groupsToLoad);

        return this.loadData(
          {
            [key]: groupsToLoad,
          },
          true
        );
      } else {
        return 0;
      }
    },
    findItemIndex(item, dataStack = undefined) {
      if (dataStack == undefined) {
        dataStack = Array.isArray(this.data) ? this.data : undefined;
      }
      if (!Array.isArray(dataStack)) {
        return false;
      }

      return typeof item == "object" && this.idKey && this.idKey in item
        ? dataStack.findIndex(
            (p) => this.idKey in p && p[this.idKey] == item[this.idKey]
          )
        : dataStack.findIndex(
            (p) => p == item || (this.idKey in p && p[this.idKey] == item)
          );
    },
    findAndRemoveOrReplace(
      item,
      replacement = undefined,
      dataStack = undefined
    ) {
      if (dataStack === undefined) {
        dataStack = Array.isArray(this.data) ? this.data : undefined;
      }
      if (!Array.isArray(dataStack)) {
        return false;
      }

      let index = this.findItemIndex(item, dataStack);

      if (index !== false && index !== -1) {
        if (replacement !== undefined) {
          dataStack.splice(index, 1, replacement);
        } else {
          dataStack.splice(index, 1);
        }

        return true;
      }

      return false;
    },
  },
};
</script>